import { MatchStatus, type Match } from "@api-gen/sports_pb";

const ongoingStatuses = new Set<MatchStatus>([
  MatchStatus.LIVE,
  MatchStatus.HALFTIME,
  MatchStatus.SUSPENDED,
]);

export const ACTIVE_DISPLAY_WINDOW_MS = 2 * 60 * 60 * 1000;

type TimestampLike = {
  seconds: bigint;
  nanos: number;
};

type ResponseMetadataLike = {
  servedAt?: TimestampLike;
};

export function responseNowMillis(
  metadata: ResponseMetadataLike | undefined,
  fallback = Date.now(),
) {
  const servedAt = metadata?.servedAt;
  if (!servedAt) return fallback;
  const value = Number(servedAt.seconds) * 1000 + servedAt.nanos / 1_000_000;
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

export function isOngoingStatus(match: Match) {
  return ongoingStatuses.has(match.status);
}

export function isOngoingMatch(match: Match) {
  return isOngoingMatchAt(match, Date.now());
}

// Keep the injected clock on a separately named function. Passing a predicate
// with an optional second parameter directly to Array.filter is unsafe because
// JavaScript supplies the array index as that argument. That previously made
// every live match look as though it started in the future.
export function isOngoingMatchAt(match: Match, nowMillis: number) {
  if (!isOngoingStatus(match)) return false;
  const suppliedClock = Number(match.clockSeconds);
  if (
    Number.isFinite(suppliedClock) &&
    suppliedClock > 0 &&
    suppliedClock <= ACTIVE_DISPLAY_WINDOW_MS / 1000
  ) {
    return true;
  }
  const startedAt = matchStartMillis(match);
  return (
    Number.isFinite(startedAt) &&
    startedAt <= nowMillis &&
    startedAt >= nowMillis - ACTIVE_DISPLAY_WINDOW_MS
  );
}

export function isUpcomingMatch(match: Match) {
  return match.status === MatchStatus.SCHEDULED;
}

export function matchStartMillis(match: Match) {
  const scheduled = match.scheduledAt;
  if (!scheduled) return Number.MAX_SAFE_INTEGER;
  return Number(scheduled.seconds) * 1000 + scheduled.nanos / 1_000_000;
}

export function mergeOngoingAndUpcoming(
  ongoing: readonly Match[],
  upcoming: readonly Match[],
  nowMillis = Date.now(),
) {
  const unique = new Map<string, Match>();
  for (const match of ongoing) {
    if (isOngoingMatchAt(match, nowMillis)) unique.set(match.matchId, match);
  }
  for (const match of upcoming) {
    if (
      isUpcomingMatch(match) &&
      matchStartMillis(match) >= nowMillis - ACTIVE_DISPLAY_WINDOW_MS &&
      !unique.has(match.matchId)
    ) {
      unique.set(match.matchId, match);
    }
  }
  return [...unique.values()].sort((left, right) => {
    const statusOrder =
      Number(isUpcomingMatch(left)) - Number(isUpcomingMatch(right));
    if (statusOrder !== 0) return statusOrder;
    const timeOrder = matchStartMillis(left) - matchStartMillis(right);
    return timeOrder !== 0
      ? timeOrder
      : left.matchId.localeCompare(right.matchId);
  });
}

export function mergeHomepageOngoingMatches(
  featured: readonly Match[],
  live: readonly Match[],
  nowMillis = Date.now(),
  limit = 8,
) {
  const unique = new Map<string, Match>();
  for (const match of [...featured, ...live]) {
    if (isOngoingMatchAt(match, nowMillis) && !unique.has(match.matchId)) {
      unique.set(match.matchId, match);
    }
  }
  return [...unique.values()]
    .sort((left, right) => {
      const timeOrder = matchStartMillis(left) - matchStartMillis(right);
      return timeOrder !== 0
        ? timeOrder
        : left.matchId.localeCompare(right.matchId);
    })
    .slice(0, Math.max(0, limit));
}

export function matchClockMinutes(match: Match, nowMillis = Date.now()) {
  const supplied = Number(match.clockSeconds);
  if (Number.isFinite(supplied) && supplied > 0 && supplied <= 12 * 60 * 60) {
    return Math.max(1, Math.floor(supplied / 60));
  }
  const startedAt = matchStartMillis(match);
  if (!Number.isFinite(startedAt) || startedAt > nowMillis) return 0;
  return Math.max(
    1,
    Math.min(120, Math.floor((nowMillis - startedAt) / 60_000)),
  );
}
